// C:\Users\clint\f1-tipping-site\frontend\src\components\PredictionForm.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';

const PredictionForm = () => {
  const { raceId: urlRaceId } = useParams();
  const navigate = useNavigate();
  
  // Add state for available races
  const [availableRaces, setAvailableRaces] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState(urlRaceId || '');
  
  const [race, setRace] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [existingPrediction, setExistingPrediction] = useState(null);
  
  // Add state for prediction status
  const [predictionsOpen, setPredictionsOpen] = useState(false);
  const [timeUntilClose, setTimeUntilClose] = useState('');
  const countdownIntervalRef = useRef(null);

const isPredictionsOpen = useCallback((race) => {
  if (!race) return false;
  
  const now = new Date();
  const raceDate = new Date(race.date);
  
  // If race date is in the past, predictions are closed
  if (raceDate < now) return false;
  
  // If predictions closing time has passed, predictions are closed
  if (race.qualifyingDateTime) {
    // Parse the datetime - this will handle timezone properly if stored correctly
    const qualifyingTime = new Date(race.qualifyingDateTime);
    
    // Verify the date is valid
    if (isNaN(qualifyingTime.getTime())) {
      console.error('Invalid qualifying datetime:', race.qualifyingDateTime);
      return false;
    }
    
    // Calculate prediction closing time (2 minutes before qualifying)
    const predictionClosingTime = new Date(qualifyingTime.getTime() - (2 * 60 * 1000));
    
    // Debug logging to check timezone handling
    console.log('Raw qualifyingDateTime from DB:', race.qualifyingDateTime);
    console.log('Qualifying time (local):', qualifyingTime.toLocaleString());
    console.log('Qualifying time (UTC):', qualifyingTime.toISOString());
    console.log('Prediction closing time (local):', predictionClosingTime.toLocaleString());
    console.log('Current time (local):', now.toLocaleString());
    console.log('Current time (UTC):', now.toISOString());
    console.log('Predictions should be closed:', predictionClosingTime < now);
    
    // Check if prediction closing time has passed
    if (predictionClosingTime < now) return false;
  }
  
  return true;
}, []);
  
  // Initialize prediction state
  const [prediction, setPrediction] = useState({
    podium: ['', '', ''],
    fastestLap: '',
    polePosition: '',
    firstRetirement: '',
    driverHeadToHead: {
      driver1: '',
      driver2: '',
      winner: ''
    },
    teamHeadToHead: {
      team1: '',
      team2: '',
      winner: ''
    }
  });

  // Format remaining time as a readable string
  const formatTimeRemaining = useCallback((ms) => {
    if (ms <= 0) return "Closed";
    
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s until predictions close`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s until predictions close`;
    } else {
      return `${seconds}s until predictions close`;
    }
  }, []);

  // Start countdown timer
const startCountdown = useCallback((closingTime) => {
  // Clear any existing interval
  if (countdownIntervalRef.current) {
    clearInterval(countdownIntervalRef.current);
  }
  
  // Verify closingTime is valid
  if (isNaN(closingTime.getTime())) {
    console.error('Invalid closing time provided to startCountdown:', closingTime);
    setTimeUntilClose("Invalid closing time");
    return;
  }
  
  // Set up new interval
  const interval = setInterval(() => {
    const now = new Date();
    const timeLeft = closingTime - now;
    
    if (timeLeft <= 0) {
      clearInterval(interval);
      setPredictionsOpen(false);
      setTimeUntilClose("Predictions closed"); // Fixed typo from "cclosed"
    } else {
      setTimeUntilClose(formatTimeRemaining(timeLeft));
    }
  }, 1000);
  
  // Save the interval reference using the ref
  countdownIntervalRef.current = interval;
}, [formatTimeRemaining]);

  // Process race data to update predictions status
const processRaceData = useCallback((raceData) => {
  if (!raceData) return;
  
  const now = new Date();
  const isOpen = isPredictionsOpen(raceData);
  
  setPredictionsOpen(isOpen);
  
  if (isOpen && raceData.qualifyingDateTime) {
    const qualifyingTime = new Date(raceData.qualifyingDateTime);
    
    // Verify the date is valid
    if (isNaN(qualifyingTime.getTime())) {
      console.error('Invalid qualifying datetime in processRaceData:', raceData.qualifyingDateTime);
      setTimeUntilClose("Invalid qualifying time");
      return;
    }
    
    // Calculate when predictions close (2 minutes before qualifying)
    const closingTime = new Date(qualifyingTime.getTime() - (2 * 60 * 1000));
    
    // Set initial countdown text
    setTimeUntilClose(formatTimeRemaining(closingTime - now));
    
    // Start the countdown timer
    startCountdown(closingTime);
  } else if (!isOpen) {
    // Predictions are closed
    setTimeUntilClose("Predictions closed");
    
    // Clear any existing countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }
}, [isPredictionsOpen, formatTimeRemaining, startCountdown]);
  

  // Fetch available races and drivers on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Fetch races
        console.log("Fetching all races");
        const racesResponse = await apiClient.get('/api/f1data/races/all');
        
        // Fetch drivers - do this first to make sure they're loaded
        console.log("Fetching all drivers");
        const driversResponse = await apiClient.get('/api/f1data/drivers');
        setDrivers(driversResponse.data);
        
        // Fetch teams
        console.log("Fetching all teams");
        const teamsResponse = await apiClient.get('/api/f1data/teams');
        setTeams(teamsResponse.data);
        
        // Once we have drivers, process races
        const processedRaces = racesResponse.data.map(race => ({
          ...race,
          predictionsOpen: isPredictionsOpen(race)
        }));
        
        console.log("Races response processed:", processedRaces);
        setAvailableRaces(processedRaces);
        
        // If we have a race ID from URL, set it as selected
        if (urlRaceId) {
          setSelectedRaceId(urlRaceId);
        } else if (processedRaces.length > 0) {
          // Find the first upcoming race
          const upcomingRace = processedRaces.find(race => race.predictionsOpen);
          if (upcomingRace) {
            setSelectedRaceId(upcomingRace._id);
          } else {
            // Otherwise select the first race
            setSelectedRaceId(processedRaces[0]._id);
          }
        }
      } catch (err) {
        console.error('Error fetching initial data:', err.response || err);
        setError(`Failed to load data: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [urlRaceId, isPredictionsOpen]);

  // Handle race selection change
  const handleRaceChange = (event) => {
    const newRaceId = event.target.value;
    setSelectedRaceId(newRaceId);
    
    // Reset prediction form
    setRace(null);
    setPrediction({
      podium: ['', '', ''],
      fastestLap: '',
      polePosition: '',
      firstRetirement: '',
      driverHeadToHead: {
        driver1: '',
        driver2: '',
        winner: ''
      },
      teamHeadToHead: {
        team1: '',
        team2: '',
        winner: ''
      }
    });
    setExistingPrediction(null);
    
    // Update URL without refreshing page
    navigate(`/predictions/${newRaceId}`, { replace: true });
  };

  // Fetch race and existing prediction when selectedRaceId changes
  useEffect(() => {
    // Exit early if no raceId is selected
    if (!selectedRaceId) {
      setLoading(false);
      return;
    }
    
    const fetchRaceData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Fetch race data
        console.log(`Fetching race data for race ID: ${selectedRaceId}`);
        const raceResponse = await apiClient.get(`/api/f1data/races/${selectedRaceId}`);
        
        console.log('Race data received:', raceResponse.data);
        const raceData = raceResponse.data;
        setRace(raceData);
        
        // Process race data to update prediction status
        processRaceData(raceData);
        
        // Fetch existing prediction
        const predictionResponse = await apiClient.get(`/api/predictions/${selectedRaceId}`);
        
        if (predictionResponse.data.hasPrediction) {
          setExistingPrediction(predictionResponse.data.prediction);
          setPrediction(predictionResponse.data.prediction.predictions);
        }
        
      } catch (err) {
        console.error('Error fetching race data:', err);
        setError('Failed to load race data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRaceData();
    
    // Clean up countdown on unmount or when selectedRaceId changes
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [selectedRaceId, processRaceData]);

  //Debug for timezone

  useEffect(() => {
  if (race?.qualifyingDateTime) {
    console.log('=== TIMEZONE DEBUGGING ===');
    console.log('Raw qualifyingDateTime from DB:', race.qualifyingDateTime);
    console.log('Type of qualifyingDateTime:', typeof race.qualifyingDateTime);
    
    const parsed = new Date(race.qualifyingDateTime);
    console.log('Parsed as Date object:', parsed);
    console.log('Is valid date:', !isNaN(parsed.getTime()));
    console.log('Parsed (user timezone):', parsed.toLocaleString());
    console.log('Parsed (UTC):', parsed.toISOString());
    
    const now = new Date();
    console.log('Current time (user timezone):', now.toLocaleString());
    console.log('Current time (UTC):', now.toISOString());
    
    const closingTime = new Date(parsed.getTime() - (2 * 60 * 1000));
    console.log('Prediction closing time (user timezone):', closingTime.toLocaleString());
    console.log('Prediction closing time (UTC):', closingTime.toISOString());
    
    console.log('Should predictions be closed?', closingTime < now);
    console.log('Time difference (minutes):', (now - closingTime) / (1000 * 60));
    console.log('=== END TIMEZONE DEBUGGING ===');
  }
}, [race]);

//raw data
useEffect(() => {
  if (race) {
    console.log('🔍 RAW RACE DATA:', {
      raceName: race.raceName,
      date: race.date,
      qualifyingDateTime: race.qualifyingDateTime,
      predictionsOpen: race.predictionsOpen,
      predictionClosingTime: race.predictionClosingTime
    });
  }
}, [race]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if raceId exists
    if (!selectedRaceId) {
      setError('No race selected. Please select a race first.');
      return;
    }
  
    // Check if predictions are still open
    if (!predictionsOpen) {
      setError('Predictions are now closed for this race');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      
      console.log('Submitting prediction data:', {
        raceId: selectedRaceId,
        predictions: prediction
      });
      
      const response = await apiClient.post(
        `/api/predictions/${selectedRaceId}`,
        { predictions: prediction }
      );
      
      setSuccess('Predictions submitted successfully!');
      setExistingPrediction(response.data.prediction);
      
    } catch (err) {
      console.error('Error submitting prediction:', err);
      setError(err.response?.data?.message || 'Failed to submit predictions. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle input changes
  const handleChange = (field, value) => {
    if (field.includes('.')) {
      // Handle nested fields (for head-to-heads)
      const [parent, child] = field.split('.');
      setPrediction(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else if (field === 'podium') {
      // Handle podium array updates
      const [, index] = value.name.match(/podium\[(\d+)\]/);
      const newPodium = [...prediction.podium];
      newPodium[parseInt(index)] = value.value;
      setPrediction(prev => ({
        ...prev,
        podium: newPodium
      }));
    } else {
      // Handle simple fields
      setPrediction(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  if (loading && !availableRaces.length) {
    return <div className="text-center py-10">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">F1 Predictions</h1>
      
      {/* Race Selection Dropdown */}
      <div className="mb-6">
        <label className="block text-white font-medium mb-2">
          Select Race
        </label>
        <select
          value={selectedRaceId}
          onChange={handleRaceChange}
          className="w-full p-2 border rounded bg-gray-800 text-white border-gray-600 bg-gray-800 text-white border-gray-600"
        >
          <option value="">Select a race</option>
          {availableRaces.map(race => (
            <option key={race._id} value={race._id}>
              {race.raceName} - {new Date(race.date).toLocaleDateString()}
              {race.predictionsOpen ? ' (Open)' : ' (Closed)'}
            </option>
          ))}
        </select>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}
      
      {/* Show loading state while fetching selected race data */}
      {loading ? (
        <div className="text-center py-10">Loading race data...</div>
      ) : !selectedRaceId ? (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-6">
          <p className="font-bold">No race selected</p>
          <p>Please select a race from the dropdown to make predictions.</p>
        </div>
      ) : !race ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">Race not found</p>
          <p>The selected race could not be loaded. Please try a different race.</p>
        </div>
      ) : (
        <>
          {/* Race details and form */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold">{race.raceName}</h2>
            <p className="text-gray-300">{new Date(race.date).toLocaleDateString()}</p>
            <p className="text-sm text-gray-500">
              {race.circuit?.name}, {race.circuit?.country}
            </p>
          </div>
          
          {/* Prediction status banner - ONLY ONE BANNER */}
          {!predictionsOpen ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <p className="font-bold">Predictions are closed</p>
              <p>Predictions close 2 minutes before qualifying starts.</p>
            </div>
          ) : (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-6">
              <p className="font-bold">Predictions are open</p>
              <p>{timeUntilClose}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-12">
            {/* Podium Predictions */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Podium Predictions</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((position, index) => (
                  <div key={index} className="mb-4">
                    <label className="block text-gray-800 font-medium mb-2">
                      P{position}
                    </label>
                    <select
                      name={`podium[${index}]`}
                      value={prediction.podium[index] || ''}
                      onChange={(e) => handleChange('podium', { name: e.target.name, value: e.target.value })}
                      className="w-full p-2 border rounded bg-gray-800 text-gray border-gray-600"
                      required
                      disabled={!predictionsOpen}
                    >
                      <option value="">Select driver</option>
                      {drivers && drivers.length > 0 ? (
                        drivers.map(driver => (
                          <option key={driver.driverId} value={driver.driverId}>
                            {driver.givenName} {driver.familyName} ({driver.code})
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No drivers available</option>
                      )}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Other Predictions */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Other Predictions</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Pole Position */}
                <div className="mb-4">
                  <label className="block text-gray-800 font-medium mb-2">
                    Pole Position
                  </label>
                  <select
                    value={prediction.polePosition || ''}
                    onChange={(e) => handleChange('polePosition', e.target.value)}
                    className="w-full p-2 border rounded bg-gray-800 text-white border-gray-600"
                    required
                    disabled={!predictionsOpen}
                  >
                    <option value="">Select driver</option>
                    {drivers && drivers.length > 0 ? (
                      drivers.map(driver => (
                        <option key={driver.driverId} value={driver.driverId}>
                          {driver.givenName} {driver.familyName} ({driver.code})
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No drivers available</option>
                    )}
                  </select>
                </div>
                
                {/* Fastest Lap */}
                <div className="mb-4">
                  <label className="block text-gray-800 font-medium mb-2">
                    Fastest Lap
                  </label>
                  <select
                    value={prediction.fastestLap || ''}
                    onChange={(e) => handleChange('fastestLap', e.target.value)}
                    className="w-full p-2 border rounded bg-gray-800 text-white border-gray-600"
                    required
                    disabled={!predictionsOpen}
                  >
                    <option value="">Select driver</option>
                    {drivers && drivers.length > 0 ? (
                      drivers.map(driver => (
                        <option key={driver.driverId} value={driver.driverId}>
                          {driver.givenName} {driver.familyName} ({driver.code})
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No drivers available</option>
                    )}
                  </select>
                </div>
                
                {/* First Retirement */}
                <div className="mb-4">
                  <label className="block text-gray-800 font-medium mb-2">
                    First Retirement
                  </label>
                  <select
                    value={prediction.firstRetirement || ''}
                    onChange={(e) => handleChange('firstRetirement', e.target.value)}
                    className="w-full p-2 border rounded bg-gray-800 text-white border-gray-600"
                    required
                    disabled={!predictionsOpen}
                  >
                    <option value="">Select driver</option>
                    {drivers && drivers.length > 0 ? (
                      drivers.map(driver => (
                        <option key={driver.driverId} value={driver.driverId}>
                          {driver.givenName} {driver.familyName} ({driver.code})
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No drivers available</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Driver Head to Head */}
            <div className="h-12"></div>
            <div className="bg-white shadow rounded-lg p-6 mt-10">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Driver Head to Head</h2>
            
            {!race?.headToHead?.drivers?.driver1 || !race?.headToHead?.drivers?.driver2 ? (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                No driver matchup has been set for this race yet.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Driver 1 - Read Only */}
                <div className="mb-4">
                    <label className="block text-black font-medium mb-2">
                    Driver 1
                    </label>
                    <div className="p-2 border rounded bg-gray-50 flex items-center text-gray-800">
                    {race.headToHead.drivers.driver1.name} ({race.headToHead.drivers.driver1.code})
                    <input 
                        type="hidden" 
                        name="driverHeadToHead.driver1"
                        value={race.headToHead.drivers.driver1.driverId} 
                        onChange={() => {}} // Empty onChange to avoid React warnings
                    />
                    </div>
                </div>
                
                {/* Driver 2 - Read Only */}
                <div className="mb-4">
                    <label className="block text-gray-800 font-medium mb-2">
                    Driver 2
                    </label>
                    <div className="p-2 border rounded bg-gray-50 flex items-center text-gray-800">
                    {race.headToHead.drivers.driver2.name} ({race.headToHead.drivers.driver2.code})
                    <input 
                        type="hidden" 
                        name="driverHeadToHead.driver2"
                        value={race.headToHead.drivers.driver2.driverId} 
                        onChange={() => {}} // Empty onChange to avoid React warnings
                    />
                    </div>
                </div>
                
                {/* Winner - Selection */}
                <div className="mb-4">
                    <label className="block text-gray-800 font-medium mb-2">
                    Winner
                    </label>
                    <select
                    value={prediction.driverHeadToHead.winner || ''}
                    onChange={(e) => handleChange('driverHeadToHead.winner', e.target.value)}
                    className="w-full p-2 border rounded bg-gray-800 text-white border-gray-600"
                    required
                    disabled={!predictionsOpen}
                    >
                    <option value="">Select winner</option>
                    <option value={race.headToHead.drivers.driver1.driverId}>
                        {race.headToHead.drivers.driver1.name} ({race.headToHead.drivers.driver1.code})
                    </option>
                    <option value={race.headToHead.drivers.driver2.driverId}>
                        {race.headToHead.drivers.driver2.name} ({race.headToHead.drivers.driver2.code})
                    </option>
                    </select>
                </div>
                </div>
            )}
            </div>
            
            {/* Team Head to Head */}
            <div className="bg-white shadow rounded-lg p-6 mt-10">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Team Head to Head</h2>
              <p className="text-sm text-black mb-4">
                Which team will have the higher average finishing position (add both driver positions and divide by 2). 
                E.g. If Team A finished 1st and 20th, and Team B finished 9th and 10th, Team B will win as their ave position was 9.5, Team A's ave was 10.5.
              </p>            
            
            {!race?.headToHead?.teams?.team1 || !race?.headToHead?.teams?.team2 ? (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                No team matchup has been set for this race yet.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Team 1 - Read Only */}
                <div className="mb-4">
                    <label className="block text-white font-medium mb-2">
                    Team 1
                    </label>
                    <div className="p-2 border rounded bg-gray-50 flex items-center text-gray-800">
                    {race.headToHead.teams.team1.name}
                    <input 
                        type="hidden" 
                        name="teamHeadToHead.team1"
                        value={race.headToHead.teams.team1.constructorId} 
                        onChange={() => {}} // Empty onChange to avoid React warnings
                    />
                    </div>
                </div>
                
                {/* Team 2 - Read Only */}
                <div className="mb-4">
                    <label className="block text-white font-medium mb-2">
                    Team 2
                    </label>
                    <div className="p-2 border rounded bg-gray-50 flex items-center text-gray-800">
                    {race.headToHead.teams.team2.name}
                    <input 
                        type="hidden" 
                        name="teamHeadToHead.team2"
                        value={race.headToHead.teams.team2.constructorId}
                        onChange={() => {}} // Empty onChange to avoid React warnings
                    />
                    </div>
                </div>
                
                {/* Winner - Selection */}
                <div className="mb-4">
                    <label className="block text-white font-medium mb-2">
                    Winner
                    </label>
                    <select
                    value={prediction.teamHeadToHead.winner || ''}
                    onChange={(e) => handleChange('teamHeadToHead.winner', e.target.value)}
                    className="w-full p-2 border rounded bg-gray-800 text-white border-gray-600"
                    required
                    disabled={!predictionsOpen}
                    >
                    <option value="">Select winner</option>
                    <option value={race.headToHead.teams.team1.constructorId}>
                        {race.headToHead.teams.team1.name}
                    </option>
                    <option value={race.headToHead.teams.team2.constructorId}>
                        {race.headToHead.teams.team2.name}
                    </option>
                    </select>
                </div>
                </div>
            )}
            </div>
            
            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                className={`px-6 py-2 rounded-md font-medium ${
                  predictionsOpen
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-400 text-white cursor-not-allowed'
                }`}
                disabled={!predictionsOpen || submitting}
              >
                {submitting ? 'Submitting...' : existingPrediction ? 'Update Predictions' : 'Submit Predictions'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default PredictionForm;